/**
 * cm-coding: Workspace administrators compare a monthly spend limit with the
 * recorded spend it controls, so policy editing stays beside that workspace's
 * cost dashboard. The browser reports input and save errors, while the backend
 * validates the policy and decides whether new processing may begin; enforcing
 * either decision here would let another client bypass the limit.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription } from '@/components/ui'
import { useApi } from '../../CostsComponent'
import { createCostsApi } from '../../services/costs-api'
import type { WorkspaceCostPolicy } from '../../types'

interface WorkspaceCostPolicyCardProps {
  organizationId: string
  workspaceId: string
  canManage: boolean
}

const policyKey = (organizationId: string, workspaceId: string) => [
  'workspace-cost-policy',
  organizationId,
  workspaceId,
]

export function WorkspaceCostPolicyCard({
  organizationId,
  workspaceId,
  canManage,
}: WorkspaceCostPolicyCardProps) {
  const { apiInstance } = useApi()
  const api = createCostsApi(apiInstance)
  const queryClient = useQueryClient()
  const [alertThreshold, setAlertThreshold] = useState('')
  const [maximum, setMaximum] = useState('')
  const [saved, setSaved] = useState({ alertThreshold: '', maximum: '' })
  const [validationError, setValidationError] = useState<string | null>(null)
  const policy = useQuery({
    queryKey: policyKey(organizationId, workspaceId),
    queryFn: () => api.getWorkspaceCostPolicy(organizationId, workspaceId),
  })

  useEffect(() => {
    if (!policy.data) return
    const next = {
      alertThreshold: policy.data.policy.monthlyAlertThresholdUsd ?? '',
      maximum: policy.data.policy.monthlyMaxCostUsd ?? '',
    }
    setAlertThreshold(next.alertThreshold)
    setMaximum(next.maximum)
    setSaved(next)
  }, [policy.data])

  const save = useMutation({
    mutationFn: (next: WorkspaceCostPolicy) => api.replaceWorkspaceCostPolicy(
      organizationId,
      workspaceId,
      next,
    ),
    onSuccess: (snapshot) => {
      queryClient.setQueryData(policyKey(organizationId, workspaceId), snapshot)
      setSaved({ alertThreshold, maximum })
      setValidationError(null)
      toast.success('Workspace cost policy saved')
    },
    onError: (cause: unknown) => toast.error(
      (cause as { response?: { data?: { detail?: string } } }).response?.data?.detail
      ?? 'Failed to save workspace cost policy',
    ),
  })

  if (policy.isPending) {
    return (
      <div
        data-testid="workspace-cost-policy-card"
        className="mt-4 flex items-center gap-2 rounded-xl border border-[#e7ecf2] bg-white px-6 py-8 text-[13px] text-slate-500 shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Loading workspace cost policy…
      </div>
    )
  }

  if (policy.isError || !policy.data) {
    return (
      <Alert variant="destructive" className="mt-4" data-testid="workspace-cost-policy-error">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Failed to load workspace cost policy.</AlertDescription>
      </Alert>
    )
  }

  const dirty = alertThreshold !== saved.alertThreshold || maximum !== saved.maximum
  const current = formatUSD(policy.data.currentMonthCostUsd)

  const submit = () => {
    const error = validatePolicy(alertThreshold, maximum)
    if (error) {
      setValidationError(error)
      return
    }
    setValidationError(null)
    save.mutate({
      monthlyAlertThresholdUsd: alertThreshold || null,
      monthlyMaxCostUsd: maximum || null,
    })
  }

  const saveDisabled = !dirty || save.isPending

  return (
    <section data-testid="workspace-cost-policy-card">
      {policy.data.status === 'alert' && (
        <div
          data-testid="workspace-cost-threshold-alert"
          className="mt-4 flex items-center gap-2.5 rounded-[10px] border border-[#fde68a] bg-[#fffbeb] px-4 py-3 text-[13px] text-[#92400e]"
        >
          <span className="h-2 w-2 flex-none rounded-full bg-[#f59e0b]" />
          <span>Monthly cost alert reached — this workspace has recorded {current} this month.</span>
        </div>
      )}
      {policy.data.status === 'blocked' && (
        <div
          data-testid="workspace-cost-blocked-alert"
          className="mt-4 flex items-center gap-2.5 rounded-[10px] border border-[#fecaca] bg-[#fef2f2] px-4 py-3 text-[13px] text-[#991b1b]"
        >
          <span className="h-2 w-2 flex-none rounded-full bg-[#ef4444]" />
          Processing is paused because this workspace reached its monthly maximum. Existing running jobs may finish.
        </div>
      )}

      <div className="mt-4 rounded-xl border border-[#e7ecf2] bg-white px-6 py-5 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold tracking-[-0.01em]">Monthly cost controls</div>
            <div className="mt-[3px] text-[12.5px] text-slate-500">
              Alert administrators or pause new processing based on recorded UTC-month spend
              {' · '}current month <span data-testid="workspace-current-month-cost">{current}</span>
            </div>
          </div>
          {canManage && (
            <div className="flex flex-none items-center gap-3">
              {!dirty && !validationError && (
                <span className="text-xs font-[550] text-[#16a34a]">✓ Settings saved</span>
              )}
              <button
                type="button"
                data-testid="workspace-cost-policy-save"
                disabled={saveDisabled}
                onClick={submit}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[12.5px] font-semibold transition-all duration-[120ms] ${
                  saveDisabled
                    ? 'cursor-default bg-slate-100 text-slate-400'
                    : 'bg-[#2563eb] text-white hover:brightness-[1.08]'
                }`}
              >
                {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save cost controls
              </button>
            </div>
          )}
        </div>
        <div className="mt-4 grid gap-5 md:grid-cols-2">
          <PolicyInput
            id="monthly-alert-threshold"
            label="Monthly alert threshold"
            description="Shows an in-app alert when recorded spend reaches this amount."
            value={alertThreshold}
            disabled={!canManage || save.isPending}
            onChange={setAlertThreshold}
          />
          <PolicyInput
            id="monthly-maximum-cost"
            label="Maximum monthly cost"
            description="Pauses new and queued processing when recorded spend reaches this amount."
            value={maximum}
            disabled={!canManage || save.isPending}
            onChange={setMaximum}
          />
        </div>
        {validationError && (
          <p className="mt-3 text-[12.5px] text-[#dc2626]" role="alert">{validationError}</p>
        )}
      </div>
    </section>
  )
}

function PolicyInput({
  id,
  label,
  description,
  value,
  disabled,
  onChange,
}: {
  id: string
  label: string
  description: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <div>
      <label htmlFor={id} className="text-[12.5px] font-[550] text-slate-700">{label}</label>
      <div className="relative mt-1.5">
        <span className="pointer-events-none absolute left-3 top-2 text-[13px] text-slate-400">$</span>
        <input
          id={id}
          data-testid={id}
          inputMode="decimal"
          placeholder="Not set"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value.trim())}
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-[26px] pr-3 text-[13px] text-slate-900 transition-[border-color,box-shadow] duration-[120ms] placeholder:text-slate-400 focus:border-[#93c5fd] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:outline-none disabled:bg-slate-50 disabled:text-slate-500"
        />
      </div>
      <p className="mt-1.5 text-[11.5px] text-slate-400">{description}</p>
    </div>
  )
}

function validatePolicy(alertThreshold: string, maximum: string): string | null {
  const money = /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/
  for (const [label, value] of [
    ['Monthly alert threshold', alertThreshold],
    ['Maximum monthly cost', maximum],
  ] as const) {
    if (value && (!money.test(value) || Number(value) <= 0)) {
      return `${label} must be a positive dollar amount with at most 6 decimal places.`
    }
  }
  if (alertThreshold && maximum && Number(alertThreshold) >= Number(maximum)) {
    return 'Monthly alert threshold must be lower than maximum monthly cost.'
  }
  return null
}

function formatUSD(value: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value))
}
